import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import IconButton from '@material-ui/core/IconButton';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import Typography from '@material-ui/core/Typography';
import Chip from '@material-ui/core/Chip';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';

const useStyles = makeStyles(theme => ({
    paginationContainer: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '12px 8px',
        marginBottom: '12px',
        gap: '8px',
        backgroundColor: theme.palette.background.paper,
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        [theme.breakpoints.down('xs')]: {
            padding: '8px 4px',
            gap: '4px'
        }
    },
    controlsCenter: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        flex: 1,
        [theme.breakpoints.down('xs')]: {
            gap: '4px'
        }
    },
    controlsRight: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        [theme.breakpoints.down('xs')]: {
            gap: '4px'
        }
    },
    navButton: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        '&:hover': {
            backgroundColor: theme.palette.primary.dark,
        },
        '&:disabled': {
            backgroundColor: theme.palette.action.disabledBackground,
            color: theme.palette.action.disabled
        },
        [theme.breakpoints.down('xs')]: {
            padding: '6px'
        }
    },
    pageInfo: {
        fontWeight: 500,
        color: theme.palette.text.secondary,
        padding: '0 8px',
        [theme.breakpoints.down('xs')]: {
            fontSize: '0.75rem',
            padding: '0 4px'
        }
    },
    pageSizeSelect: {
        marginLeft: '8px',
        '& .MuiSelect-select': {
            padding: '6px 24px 6px 8px',
            fontSize: '0.875rem'
        },
        [theme.breakpoints.down('xs')]: {
            marginLeft: '4px',
            '& .MuiSelect-select': {
                padding: '4px 20px 4px 6px',
                fontSize: '0.75rem'
            }
        }
    },
    chip: {
        marginLeft: '4px',
        height: '24px',
        [theme.breakpoints.down('xs')]: {
            height: '20px',
            fontSize: '0.7rem'
        }
    }
}));

/**
 * Modern pagination component with page size control
 * 
 * @param {number} currentPage - Current page number (1-indexed)
 * @param {number} totalItems - Total number of items
 * @param {number} pageSize - Number of items per page
 * @param {Function} onPageChange - Callback when page changes: (newPage) => void
 * @param {Function} onPageSizeChange - Callback when page size changes: (newSize) => void
 * @param {Array} pageSizeOptions - Array of page size options (default: [25, 50, 100])
 * @param {React.ReactNode} filterButton - Optional filter button to render on the right
 */
const Pagination = ({
    currentPage,
    totalItems,
    pageSize,
    onPageChange,
    onPageSizeChange,
    pageSizeOptions = [25, 50, 100],
    filterButton
}) => {
    const classes = useStyles();
    
    const totalPages = Math.ceil(totalItems / pageSize);
    const startItem = totalItems === 0 ? 0 : (currentPage - 1) * pageSize + 1;
    const endItem = Math.min(currentPage * pageSize, totalItems);
    
    const handlePrevious = () => {
        if (currentPage > 1) {
            onPageChange(currentPage - 1);
        }
    };
    
    const handleNext = () => {
        if (currentPage < totalPages) {
            onPageChange(currentPage + 1);
        }
    };
    
    const handlePageSizeChange = (event) => {
        onPageSizeChange(event.target.value);
        // Reset to page 1 when page size changes
        onPageChange(1);
    };
    
    return (
        <div className={classes.paginationContainer}>
            <div className={classes.controlsCenter}>
                <IconButton 
                    onClick={handlePrevious}
                    disabled={currentPage <= 1}
                    className={classes.navButton}
                    size="small"
                    aria-label="Previous page"
                >
                    <ChevronLeftIcon />
                </IconButton>
                
                <Typography variant="body2" className={classes.pageInfo}>
                    {startItem}-{endItem} of {totalItems}
                </Typography>
                
                {totalPages > 1 && (
                    <Chip 
                        label={`${currentPage}/${totalPages}`} 
                        size="small" 
                        color="primary"
                        variant="outlined"
                        className={classes.chip}
                    />
                )}
                
                <IconButton 
                    onClick={handleNext}
                    disabled={currentPage >= totalPages}
                    className={classes.navButton}
                    size="small"
                    aria-label="Next page"
                >
                    <ChevronRightIcon />
                </IconButton>
                
                <Select
                    value={pageSize}
                    onChange={handlePageSizeChange}
                    className={classes.pageSizeSelect}
                    variant="outlined"
                    aria-label="Items per page"
                >
                    {pageSizeOptions.map(size => (
                        <MenuItem key={size} value={size}>{size}/page</MenuItem>
                    ))}
                </Select>
            </div>
            
            {filterButton && (
                <div className={classes.controlsRight}>
                    {filterButton}
                </div>
            )}
        </div>
    );
};

export default Pagination;
